import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { getService } from "@/data/servicesData";
import useSeo from "@/lib/useSeo";
import { serviceSchema, breadcrumbSchema } from "@/lib/schema";

export default function ServiceDetail() {
  const { slug } = useParams();
  const svc = getService(slug);

  useSeo({
    title: svc ? svc.seo_title : "Service not found | ClariDent RCM",
    description: svc ? svc.seo_description : "The service you are looking for could not be found.",
    path: svc ? `/services/${svc.slug}` : undefined,
    robots: svc ? "index, follow" : "noindex, follow",
    schemas: svc
      ? [
          serviceSchema(svc),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: svc.title, path: `/services/${svc.slug}` },
          ]),
        ]
      : [],
  });

  if (!svc) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="service-not-found">
        <h1 className="font-display text-4xl font-bold text-navy-900">Service not found</h1>
        <Link to="/" className="mt-6 inline-block text-teal-600 font-bold text-sm">Back to homepage</Link>
      </main>
    );
  }

  return (
    <main data-testid="service-detail-page">
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Link to="/services" className="inline-flex items-center gap-2 text-sm font-bold text-teal-600 mb-8 hover:gap-3 transition-all" data-testid="service-back-link">
            <ArrowLeft size={16} /> All services
          </Link>
          <div className="flex items-center gap-5 mb-6">
            <div className="h-16 w-16 rounded-2xl bg-teal-600 flex items-center justify-center shadow-lg">
              <svc.icon size={30} className="text-white" />
            </div>
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600">ClariDent RCM Services</p>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter font-black text-navy-900" data-testid="service-title">{svc.title}</h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">{svc.tagline}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-14 items-start">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h2 className="font-display text-3xl font-bold text-navy-900 mb-6">Why it matters</h2>
          {svc.intro.map((p) => (
            <p key={p.slice(0, 30)} className="text-base sm:text-lg text-slate-600 leading-relaxed mb-5">{p}</p>
          ))}
          <div className="mt-8 rounded-2xl bg-teal-50 border border-teal-100 p-7" data-testid="service-outcome">
            <p className="text-xs font-bold uppercase tracking-widest text-teal-600 mb-2">The outcome</p>
            <p className="text-sm sm:text-base text-navy-900 font-semibold leading-relaxed">{svc.outcome}</p>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <h2 className="font-display text-2xl font-bold text-navy-900 mb-6">What's included</h2>
          <ul className="space-y-3" data-testid="service-included-list">
            {svc.items.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-slate-600">
                <CheckCircle2 size={19} className="text-teal-600 shrink-0 mt-0.5" /> {item}
              </li>
            ))}
          </ul>
        </motion.div>
      </section>

      <section className="bg-navy-950 py-20 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-12" data-testid="service-process-heading">How we work</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {svc.steps.map((s, i) => (
              <motion.div
                key={s.t}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="rounded-2xl border border-slate-800 bg-navy-900/60 p-8"
                data-testid={`service-step-${i}`}
              >
                <p className="font-display text-4xl font-black text-teal-500/40">0{i + 1}</p>
                <h3 className="font-display text-xl font-semibold mt-4">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="rounded-3xl bg-navy-950 px-8 py-14 sm:px-16 text-center relative overflow-hidden" data-testid="service-cta">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(14,143,143,0.25),transparent_65%)]" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
              See what {svc.title.toLowerCase().includes("verification") ? "verification" : "this"} could fix in your practice.
            </h2>
            <p className="mt-4 text-slate-400 text-sm sm:text-base max-w-xl mx-auto">
              Start with a free RCM audit — we'll review your claims, denials, and AR and show you exactly where this service pays for itself.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/book" data-testid="service-cta-audit-btn" className="rounded-full bg-teal-600 px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-500 hover:-translate-y-1 hover:shadow-xl">
                Get Your Free RCM Audit
              </Link>
              <Link to="/contact" data-testid="service-cta-specialist-btn" className="rounded-full border-2 border-white/80 px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-white/10 inline-flex items-center gap-2">
                Talk to an RCM Specialist <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
